import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowLeft, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useOverlayViewport } from "@/hooks/use-overlay-viewport";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    mobilePresentation?: "dialog" | "fullscreen";
  }
>(({ className, style, children, mobilePresentation = "dialog", onOpenAutoFocus, ...props }, ref) => {
  const viewport = useOverlayViewport();
  const { t } = useTranslation("common");
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const footerObserver = React.useRef<ResizeObserver | null>(null);
  const fullscreen = mobilePresentation === "fullscreen";
  React.useEffect(() => {
    if (!viewport.isMobile || !fullscreen) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && scrollRef.current?.contains(active)) {
      const frame = requestAnimationFrame(() => active.scrollIntoView({ block: "nearest" }));
      return () => cancelAnimationFrame(frame);
    }
  }, [viewport.height, viewport.isMobile, fullscreen]);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={node => {
          contentRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        aria-describedby={undefined}
        className={cn(
          "overlay-viewport dialog-layer fixed inset-0 z-50 grid place-items-center p-4 overflow-y-auto pointer-events-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out"
        )}
        style={{ ...viewport.style, ...style }}
        data-mobile-presentation={mobilePresentation}
        onOpenAutoFocus={event => {
          onOpenAutoFocus?.(event);
          if (fullscreen && window.matchMedia("(max-width: 1023px)").matches && !event.defaultPrevented) {
            event.preventDefault();
            contentRef.current?.focus();
          }
        }}
        {...props}
      >
        <div 
          className={cn(
            "dialog-surface pointer-events-auto relative w-full max-w-lg border border-border dark:border-white/40 bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg p-6",
            className
          )}
        >
          {fullscreen && <div className="dialog-mobile-bar">
            <DialogPrimitive.Close className="flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-medium hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <ArrowLeft className="h-5 w-5" aria-hidden />
              {t("close")}
            </DialogPrimitive.Close>
          </div>}
          <div ref={node => {
            scrollRef.current = node;
            footerObserver.current?.disconnect();
            if (!node || !fullscreen || !viewport.isMobile) return;
            const footer = node.querySelector<HTMLElement>("[data-dialog-footer]");
            if (!footer) return;
            const update = () => node.style.setProperty("--dialog-actions-height", `${footer.getBoundingClientRect().height}px`);
            footerObserver.current = new ResizeObserver(update);
            footerObserver.current.observe(footer);
            update();
          }} className="dialog-scroll">{children}</div>
          <DialogPrimitive.Close className="dialog-close absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">{t("close")}</span>
          </DialogPrimitive.Close>
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div data-dialog-footer className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
